import { ScaledImageSize } from '@cf-shared/data-model/scaled-image';

// Angular-free helper for building public image download URLs. Kept clear of
// Angular imports so the URL rule is unit-testable under mocha/ts-node — see
// image-url-helpers.test.ts. Sibling of image-upload-helpers.ts.

// The server's storageGetDownloadUrl builds plain public object URLs:
//   https://storage.googleapis.com/<bucket>/<path>
// (no token, no expiry — the images bucket has uniform bucket-level access
// with public read via IAM; see
// server/functions/src/functions/storageGetDownloadUrl.ts). This mirrors that
// rule client-side so the offline upload outbox (#3989) can hand callers
// their final URL before the bytes have reached the server.
//
// `path` is a GCS object key WITHOUT a leading slash (buildImageVariantKey's
// shape, e.g. "images/food-images/<hash>__w=1080.jpg") — a leading slash
// would bake an empty path segment into the URL ("<bucket>//images/...",
// issue #3707).
export function buildPublicDownloadUrl(storageBucket: string, path: string): string {
  return `https://storage.googleapis.com/${storageBucket}/${path}`;
}

// Inverse of buildPublicDownloadUrl (#3989 follow-up: queued offline photos
// must display immediately). The outbox-aware <img> error guards receive the
// URL that just FAILED to load and need the GCS object key it points at, to
// ask the outbox whether those bytes are still queued on this device.
//
// Percent-encoding: food document ids contain '+' (e.g. "food/+UDF-<hash>"),
// and the same object key reaches an <img> both raw ("/images/food/+UDF-...")
// and percent-encoded ("/images/food/%2BUDF-...") depending on who built the
// URL. Outbox doc ids store the RAW key, so the encoded form must be decoded
// before lookup. decodeURIComponent leaves a raw '+' untouched (the
// plus-means-space rule is query-string-only), so decoding is safe for both
// forms.
//
// Returns null for anything that is not a public object URL of the given
// bucket — foreign hosts, other buckets, blob:/data: URLs (an already-swapped
// local preview must not loop back into the guard), and malformed encodings.
export function derivePathFromPublicUrl(storageBucket: string, url: string): string | null {
  const prefix = `https://storage.googleapis.com/${storageBucket}/`;
  if (!url || !url.startsWith(prefix)) {
    return null;
  }
  const rawPath = url.slice(prefix.length);
  if (rawPath.length === 0) {
    return null;
  }
  try {
    return decodeURIComponent(rawPath);
  } catch {
    // malformed percent-encoding (e.g. a stray '%') — not a key we can look up
    return null;
  }
}

// The markdown image reference an editor inserts into an exercise description
// or a global text document, for the largest size of a just-uploaded image.
//
// The reference is the GCS object key, percent-encoded as one component:
//   ![](images%2Fo%2F<owner>%2Fexercise-images%2F<hash>__w%3D1080.jpg)
// That is the only storage-image form the markdown renderer accepts
// (markdown-rewrite.ts matches src="images%2F…"), and cs-markdown resolves it
// to a download URL through storageGetDownloadUrl at display time.
//
// It used to be cut out of the download URL with /(images.+)\?/, which matched
// the old Firebase URLs (…/o/images%2F…?alt=media&token=…) and throws on
// today's plain public URLs, which have no query string
// (deferred finding df-be3442acde11).
//
// Returns null when no object key can be found — no sizes at all, or a size
// with neither a path nor a public URL of this bucket — so the caller can
// report it instead of inserting a broken reference.
export function buildMarkdownImageTag(storageBucket: string, sizes: ScaledImageSize[] | undefined): string | null {
  // if there are no sizes, there is nothing to reference
  if (!sizes || sizes.length === 0) {
    return null;
  }
  const largest = sizes[sizes.length - 1];
  // if the stored path is missing, work it back out of the public URL
  const path = largest?.path || (largest?.url ? derivePathFromPublicUrl(storageBucket, largest.url) : null);
  if (!path) {
    return null;
  }
  return `![](${encodeURIComponent(path)})`;
}
